import React from 'react';
import { Modal, Table, Input, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { displayRoleName, ROLE_TAG_COLOR } from '../../utils/role';
import { modalWidth } from '../../utils/responsive';
import './MediaAccessUserPicker.css';

/**
 * 媒体权限：浏览用户列表并多选分配（弹窗）
 */
class MediaAccessUserPicker extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            keyword: '',
            selectedRowKeys: [],
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState({ keyword: '', selectedRowKeys: [] });
        }
    }

    getAssignedSet = () => new Set(this.props.assignedUserIds || []);

    getFilteredUsers = () => {
        const { users = [] } = this.props;
        const { keyword } = this.state;
        const assignedSet = this.getAssignedSet();
        const kw = keyword.trim().toLowerCase();
        const list = users.filter((u) => !assignedSet.has(u.id));
        if (!kw) return list;
        return list.filter(
            (u) =>
                (u.loginId && u.loginId.toLowerCase().includes(kw)) ||
                (u.email && u.email.toLowerCase().includes(kw)) ||
                (u.phone && u.phone.includes(kw)) ||
                (displayRoleName(u) && displayRoleName(u).toLowerCase().includes(kw))
        );
    };

    handleOk = () => {
        const { selectedRowKeys } = this.state;
        if (selectedRowKeys.length === 0) {
            return;
        }
        this.props.onConfirm(selectedRowKeys);
    };

    render() {
        const {
            open,
            title = '选择用户',
            resourceLabel,
            actionLabel = '浏览',
            loading = false,
            confirming = false,
            onCancel,
        } = this.props;
        const { keyword, selectedRowKeys } = this.state;
        const filteredUsers = this.getFilteredUsers();
        const assignedSet = this.getAssignedSet();

        return (
            <Modal
                className="media-access-user-picker"
                title={title}
                open={open}
                onCancel={onCancel}
                onOk={this.handleOk}
                okText={selectedRowKeys.length > 0 ? `分配 ${selectedRowKeys.length} 个用户` : '分配'}
                cancelText="取消"
                confirmLoading={confirming}
                okButtonProps={{ disabled: selectedRowKeys.length === 0 }}
                width={modalWidth(640)}
                destroyOnClose
            >
                {resourceLabel && (
                    <Typography.Paragraph type="secondary" className="media-access-user-picker__hint">
                        为「{resourceLabel}」分配{actionLabel}权限，已分配用户不在下列表中。
                    </Typography.Paragraph>
                )}

                <div className="media-access-user-picker__toolbar">
                    <Input
                        allowClear
                        placeholder="搜索登录名、手机、邮箱"
                        prefix={<SearchOutlined />}
                        value={keyword}
                        onChange={(e) => this.setState({ keyword: e.target.value })}
                        style={{ maxWidth: 320 }}
                    />
                    <span className="media-access-user-picker__hint">
                        已选 {selectedRowKeys.length} / 可选 {filteredUsers.length}
                    </span>
                </div>

                <Table
                    className="media-access-user-picker__table"
                    size="small"
                    rowKey="id"
                    loading={loading}
                    dataSource={filteredUsers}
                    pagination={{ pageSize: 8, showSizeChanger: false, showTotal: (t) => `共 ${t} 人` }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => this.setState({ selectedRowKeys: keys }),
                        getCheckboxProps: () => ({ disabled: confirming }),
                    }}
                    columns={[
                        { title: '登录名', dataIndex: 'loginId', width: 140 },
                        {
                            title: '角色',
                            dataIndex: 'roleKey',
                            width: 120,
                            render: (_, row) => (
                                <Tag
                                    color={ROLE_TAG_COLOR[row.roleKey]}
                                    className="media-access-user-picker__assigned-tag"
                                >
                                    {displayRoleName(row)}
                                </Tag>
                            ),
                        },
                        { title: '手机', dataIndex: 'phone', width: 130, render: (v) => v || '—' },
                        { title: '邮箱', dataIndex: 'email', ellipsis: true, render: (v) => v || '—' },
                    ]}
                    locale={{
                        emptyText:
                            assignedSet.size > 0 ? '暂无可分配用户（均已分配）' : '暂无用户',
                    }}
                    scroll={{ x: 'max-content', y: 360 }}
                />
            </Modal>
        );
    }
}

export default MediaAccessUserPicker;
